import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "./axiosBaseQuery";

const baseQuery = axiosBaseQuery();

export const djangoApi = createApi({
  reducerPath: "djangoApi",
  baseQuery,
  tagTypes: ["Device", "BulkStatus", "APIKey", "Organization"],
  endpoints: (builder) => ({
    getDevices: builder.query({
      query: ({
        limit = 25,
        offset = 0,
        search = "",
        organization,
        connection_status,
      } = {}) => ({
        url: "devices/",
        params: {
          limit,
          offset,
          ...(search && { search }),
          ...(organization && { organization }),
          ...(connection_status && { connection_status }),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ serial_number }) => ({
                type: "Device",
                id: serial_number,
              })),
              { type: "Device", id: "LIST" },
            ]
          : [{ type: "Device", id: "LIST" }],
    }),
    getDevice: builder.query({
      query: (sn) => {
        return { url: "devices/" + sn };
      },
      providesTags: (result, error, arg) => [{ type: "Device", id: arg }],
    }),
    getDeviceConnectionDetails: builder.query({
      query: (sn) => ({ url: `devices/${sn}/connection_details/` }),
    }),
    getDeviceMessageHistory: builder.query({
      query: (sn) => ({ url: `devices/${sn}/message_history/` }),
      providesTags: (result, error, sn) => [
        { type: "Device", id: `${sn}-messages` },
      ],
    }),
    rebootDevice: builder.mutation({
      query: ({ sn, data }) => ({
        url: "devices/" + sn + "/reboot/",
        method: "post",
        data,
      }),
      // No tag invalidation — WebSocket pushes live status updates
    }),
    updateDevice: builder.mutation({
      query: ({ sn, data }) => ({
        url: "devices/" + sn + "/update_firmware/",
        method: "post",
        data,
      }),
      // No tag invalidation — WebSocket pushes live status updates
    }),
    powerOnDevice: builder.mutation({
      query: ({ sn, data }) => ({
        url: "devices/" + sn + "/power_on/",
        method: "post",
        data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Device", id: arg.sn }],
    }),
    powerOffDevice: builder.mutation({
      query: ({ sn, data }) => ({
        url: "devices/" + sn + "/power_off/",
        method: "post",
        data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Device", id: arg.sn }],
    }),
    modifyDevice: builder.mutation({
      query: ({ sn, data }) => ({
        url: `devices/${sn}/`,
        method: "patch",
        data: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Device", id: arg.sn }],
    }),
    provisionDevices: builder.mutation({
      query: (body) => ({
        url: "devices/provision/",
        method: "post",
        data: body,
      }),
      invalidatesTags: [{ type: "Device", id: "LIST" }],
    }),
    previewProvision: builder.mutation({
      query: (devices) => ({
        url: "devices/provision_preview/",
        method: "post",
        data: { devices },
      }),
    }),
    getOrganizations: builder.query({
      query: () => ({ url: "organizations/" }),
      providesTags: ["Organization"],
    }),
    createOrganization: builder.mutation({
      query: (data) => ({ url: "organizations/", method: "post", data }),
      invalidatesTags: ["Organization"],
    }),
    updateOrganization: builder.mutation({
      query: ({ id, data }) => ({
        url: `organizations/${id}/`,
        method: "patch",
        data,
      }),
      invalidatesTags: ["Organization"],
    }),
    deleteOrganization: builder.mutation({
      query: (id) => ({ url: `organizations/${id}/`, method: "delete" }),
      invalidatesTags: ["Organization"],
    }),
    addUserToOrg: builder.mutation({
      query: ({ orgId, userId }) => ({
        url: `organizations/${orgId}/add_user/`,
        method: "post",
        data: { user_id: userId },
      }),
      invalidatesTags: ["Organization"],
    }),
    removeUserFromOrg: builder.mutation({
      query: ({ orgId, userId }) => ({
        url: `organizations/${orgId}/remove_user/`,
        method: "post",
        data: { user_id: userId },
      }),
      invalidatesTags: ["Organization"],
    }),
    getUsers: builder.query({
      query: () => ({ url: "users/" }),
    }),
    getBulkStatus: builder.query({
      query: ({ organization } = {}) => ({
        url: "devices/bulk_status/",
        params: { ...(organization && { organization }) },
      }),
      providesTags: ["BulkStatus"],
    }),
    bulkReboot: builder.mutation({
      query: ({ serial_numbers }) => ({
        url: "devices/bulk_reboot/",
        method: "post",
        data: { serial_numbers },
      }),
      invalidatesTags: ["BulkStatus", { type: "Device", id: "LIST" }],
    }),
    requestDeviceStatus: builder.mutation({
      query: ({ sn }) => ({
        url: `devices/${sn}/request_status/`,
        method: "post",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Device", id: arg.sn }],
    }),
    getApiKeys: builder.query({
      query: () => ({ url: "api-keys/" }),
      providesTags: ["APIKey"],
    }),
    createApiKey: builder.mutation({
      query: (data) => ({ url: "api-keys/", method: "post", data }),
      invalidatesTags: ["APIKey"],
    }),
    revokeApiKey: builder.mutation({
      query: (id) => ({ url: `api-keys/${id}/`, method: "delete" }),
      invalidatesTags: ["APIKey"],
    }),
  }),
});

export const {
  useGetDevicesQuery,
  useGetDeviceQuery,
  useGetDeviceConnectionDetailsQuery,
  useGetDeviceMessageHistoryQuery,
  usePowerOffDeviceMutation,
  usePowerOnDeviceMutation,
  useRebootDeviceMutation,
  useUpdateDeviceMutation,
  useModifyDeviceMutation,
  useProvisionDevicesMutation,
  usePreviewProvisionMutation,
  useGetOrganizationsQuery,
  useGetBulkStatusQuery,
  useBulkRebootMutation,
  useRequestDeviceStatusMutation,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useAddUserToOrgMutation,
  useRemoveUserFromOrgMutation,
  useGetUsersQuery,
} = djangoApi;
